import { FC } from 'react';
import { css } from '@emotion/react';
import { spacing, colors, fontStyles } from '../../../styles';
import { ToolRendererProps } from './types';
import { LoadingState } from './shared/LoadingState';

interface Position {
  symbol: string;
  shares?: number;
  avg_cost?: number;
  current_price?: number;
  allocation_pct?: number;
}

interface Portfolio {
  ai_model?: string;
  ai_model_display?: string;
  account_value?: number;
  cash?: number;
  return_pct?: number;
  pnl?: number;
  positions?: Position[];
}

interface RecentTrade {
  ai_model?: string;
  ai_model_display?: string;
  symbol?: string;
  action?: string;
  rationale?: string;
}

interface AiTradeIdeasOutput {
  portfolios?: Portfolio[];
  recent_trades?: RecentTrade[];
  metadata?: {
    models_included?: string[];
    total_trades?: number;
    total_portfolios?: number;
    timestamp?: string;
  };
}

const formatAccountValue = (value: number): string => {
  if (value >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(2)}B`;
  }
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`;
  }
  return `$${value.toLocaleString()}`;
};

const getTopPosition = (positions?: Position[]): Position | null => {
  if (!positions || positions.length === 0) return null;
  return [...positions].sort((a, b) => (b.allocation_pct ?? 0) - (a.allocation_pct ?? 0))[0];
};

const getActionColor = (action?: string): string => {
  if (!action) return colors.silver;
  const upper = action.toUpperCase();
  if (upper === 'BUY') return colors.petrolBlue;
  if (upper === 'SELL') return '#e05252';
  return colors.silver;
};

// AI Trade Ideas Renderer
export const AiTradeIdeasRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  if (isLoading) {
    return <LoadingState message="Fetching AI trade ideas..." />;
  }

  const data = output as AiTradeIdeasOutput;
  const portfolios = data.portfolios ?? [];
  const recentTrades = data.recent_trades ?? [];

  const hasPortfolios = portfolios.length > 0;
  const hasTrades = recentTrades.length > 0;

  if (!hasPortfolios && !hasTrades) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        No AI trade ideas available at this time.
      </div>
    );
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.deci};
      `}
    >
      {/* Portfolio Cards */}
      {hasPortfolios && (
        <div>
          <div
            css={css`
              font-size: 12px;
              color: ${colors.silver};
              margin-bottom: ${spacing.centi};
              text-transform: uppercase;
              letter-spacing: 0.5px;
            `}
          >
            AI Portfolios
          </div>
          <div
            css={css`
              display: flex;
              flex-wrap: wrap;
              gap: ${spacing.centi};
            `}
          >
            {portfolios.map((portfolio, index) => {
              const topPosition = getTopPosition(portfolio.positions);
              const returnPct = portfolio.return_pct ?? null;
              const isPositive = (returnPct ?? 0) >= 0;

              return (
                <div
                  key={index}
                  css={css`
                    flex: 1 1 160px;
                    min-width: 140px;
                    max-width: 220px;
                    padding: ${spacing.centi};
                    border: 1px solid ${colors.cloud};
                    border-radius: 8px;
                    background: ${colors.snow};

                    html[data-theme='dark'] & {
                      border-color: ${colors.tungsten};
                      background: rgba(255, 255, 255, 0.03);
                    }
                  `}
                >
                  {/* Model name */}
                  <div
                    css={css`
                      ${fontStyles.cat}
                      font-weight: 600;
                      color: ${colors.liquorice};
                      margin-bottom: ${spacing.milli};
                      display: flex;
                      align-items: center;
                      gap: 4px;

                      html[data-theme='dark'] & {
                        color: ${colors.sugar};
                      }
                    `}
                  >
                    <span>🤖</span>
                    <span>{portfolio.ai_model_display ?? portfolio.ai_model ?? 'Unknown'}</span>
                  </div>

                  {/* Account value */}
                  {portfolio.account_value != null && (
                    <div
                      css={css`
                        font-size: 16px;
                        font-weight: 600;
                        color: ${colors.liquorice};
                        margin-bottom: 2px;

                        html[data-theme='dark'] & {
                          color: ${colors.sugar};
                        }
                      `}
                    >
                      {formatAccountValue(portfolio.account_value)}
                    </div>
                  )}

                  {/* Return */}
                  {returnPct != null && (
                    <div
                      css={css`
                        font-size: 13px;
                        font-weight: 500;
                        color: ${isPositive ? colors.petrolBlue : '#e05252'};
                        margin-bottom: ${spacing.milli};
                      `}
                    >
                      {isPositive ? '+' : ''}
                      {returnPct.toFixed(1)}%
                    </div>
                  )}

                  {/* Top position */}
                  {topPosition && (
                    <div
                      css={css`
                        font-size: 11px;
                        color: ${colors.silver};
                      `}
                    >
                      Top:{' '}
                      <span
                        css={css`
                          color: ${colors.liquorice};
                          font-weight: 500;

                          html[data-theme='dark'] & {
                            color: ${colors.fog};
                          }
                        `}
                      >
                        {topPosition.symbol}
                      </span>{' '}
                      {topPosition.allocation_pct != null ? `${topPosition.allocation_pct.toFixed(0)}%` : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Moves */}
      {hasTrades && (
        <div>
          <div
            css={css`
              font-size: 12px;
              color: ${colors.silver};
              margin-bottom: ${spacing.centi};
              text-transform: uppercase;
              letter-spacing: 0.5px;
              padding-bottom: ${spacing.milli};
              border-bottom: 1px solid ${colors.cloud};

              html[data-theme='dark'] & {
                border-bottom-color: ${colors.tungsten};
              }
            `}
          >
            Recent Moves
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${spacing.milli};
            `}
          >
            {recentTrades.map((trade, index) => {
              const actionColor = getActionColor(trade.action);
              const rationale = trade.rationale
                ? trade.rationale.length > 80
                  ? trade.rationale.slice(0, 80) + '…'
                  : trade.rationale
                : null;

              return (
                <div
                  key={index}
                  css={css`
                    display: flex;
                    align-items: flex-start;
                    gap: ${spacing.centi};
                    padding: ${spacing.milli} 0;
                  `}
                >
                  {/* Action badge */}
                  <span
                    css={css`
                      font-size: 11px;
                      font-weight: 700;
                      color: ${actionColor};
                      background: ${actionColor}18;
                      padding: 2px 6px;
                      border-radius: 4px;
                      min-width: 36px;
                      text-align: center;
                      flex-shrink: 0;
                      margin-top: 1px;
                    `}
                  >
                    {trade.action ?? '—'}
                  </span>

                  {/* Symbol */}
                  <span
                    css={css`
                      font-size: 14px;
                      font-weight: 600;
                      color: ${colors.liquorice};
                      min-width: 44px;
                      flex-shrink: 0;

                      html[data-theme='dark'] & {
                        color: ${colors.sugar};
                      }
                    `}
                  >
                    {trade.symbol ?? '—'}
                  </span>

                  {/* Model + rationale */}
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: 2px;
                      min-width: 0;
                    `}
                  >
                    <span
                      css={css`
                        font-size: 12px;
                        font-weight: 500;
                        color: ${colors.silver};
                      `}
                    >
                      {trade.ai_model_display ?? trade.ai_model ?? 'Unknown'}
                    </span>
                    {rationale && (
                      <span
                        css={css`
                          font-size: 12px;
                          color: ${colors.silver};
                          font-style: italic;
                          overflow: hidden;
                          text-overflow: ellipsis;
                          white-space: nowrap;
                        `}
                      >
                        &ldquo;{rationale}&rdquo;
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
